import React from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import {alpha, useTheme} from '@mui/material/styles';
import {
  addDays,
  addMonths,
  DateInput,
  FormField,
  formatIsoDate,
  getCalendarDays,
  isDateWithinBounds,
  parseIsoDate,
  toIsoDate,
} from 'wobi-web-common/dist/v2/index.js';
import './TripDatePicker.css';

const normalizeIso = value => String(value || '').slice(0, 10);

function CalendarIcon() {
  return (
    <svg
      aria-hidden='true' width='22' height='22' viewBox='0 0 24 24' fill='none'
      stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'
    >
      <path d='M8 2v4' /><path d='M16 2v4' /><path d='M3 10h18' />
      <path d='M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z' />
    </svg>
  );
}

function Chevron({direction}) {
  return (
    <svg
      aria-hidden='true' width='16' height='16' viewBox='0 0 24 24' fill='none'
      stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'
    >
      <path d={direction === 'previous' ? 'M9 18l6-6-6-6' : 'M15 18l-6-6 6-6'} />
    </svg>
  );
}

Chevron.propTypes = {direction: PropTypes.oneOf(['previous', 'next']).isRequired};

function laterDate(first, second) {
  if (!first) return second;
  if (!second) return first;
  return first > second ? first : second;
}

function earlierDate(first, second) {
  if (!first) return second;
  if (!second) return first;
  return first < second ? first : second;
}

function monthStart(value) {
  return normalizeIso(value).replace(/-\d{2}$/, '-01');
}

function monthEnd(value) {
  const date = parseIsoDate(monthStart(value));
  return toIsoDate(new Date(date.getFullYear(), date.getMonth() + 1, 0));
}

function RangeCalendar({
  activeField,
  startValue,
  endValue,
  hoverValue,
  minDate,
  maxDate,
  minYear,
  maxYear,
  onChange,
  onHover,
  labels,
}) {
  const initialValue = activeField === 'return' ? endValue || startValue : startValue;
  const minimumDate = laterDate(normalizeIso(minDate), minYear ? `${minYear}-01-01` : '');
  const maximumDate = earlierDate(normalizeIso(maxDate), maxYear ? `${maxYear}-12-31` : '');
  const firstVisibleDate = initialValue || minimumDate || toIsoDate(new Date());
  const [viewDate, setViewDate] = React.useState(monthStart(firstVisibleDate));
  const [focusedDate, setFocusedDate] = React.useState(initialValue || minimumDate || toIsoDate(new Date()));
  const [calendarView, setCalendarView] = React.useState('days');
  const [selectedYear, setSelectedYear] = React.useState(parseIsoDate(firstVisibleDate).getFullYear());
  const dayRefs = React.useRef(new Map());
  const selectorRefs = React.useRef(new Map());
  const days = getCalendarDays(viewDate);
  const rangeEnd = activeField === 'return' && hoverValue && hoverValue >= startValue ? hoverValue : endValue;
  const view = parseIsoDate(viewDate);
  const monthFormatter = new Intl.DateTimeFormat('he-IL', {month: 'long', year: 'numeric'});
  const monthNameFormatter = new Intl.DateTimeFormat('he-IL', {month: 'long'});
  const dayFormatter = new Intl.DateTimeFormat('he-IL', {day: 'numeric', month: 'long', year: 'numeric'});
  const weekdayFormatter = new Intl.DateTimeFormat('he-IL', {weekday: 'short'});
  const weekdays = Array.from({length: 7}, (_, index) => weekdayFormatter.format(addDays('2024-01-07', index)));
  const years = [minYear, maxYear].filter((year, index, values) => year && values.indexOf(year) === index);
  const selectorMinimumMonth = monthStart(minimumDate || toIsoDate(new Date()));
  const selectorMaximumMonth = monthStart(addMonths(selectorMinimumMonth, 18));
  const months = Array.from({length: 12}, (_, index) => {
    const start = `${selectedYear}-${String(index + 1).padStart(2, '0')}-01`;
    const end = monthEnd(start);
    const enabled = start <= selectorMaximumMonth && end >= selectorMinimumMonth &&
      (!minimumDate || end >= minimumDate) && (!maximumDate || start <= maximumDate);
    return {
      index,
      start,
      enabled,
      label: monthNameFormatter.format(parseIsoDate(start)),
    };
  });

  React.useEffect(() => {
    const selectedDate = activeField === 'return' ? endValue || startValue : startValue;
    if (!selectedDate) return;
    setViewDate(monthStart(selectedDate));
    setFocusedDate(selectedDate);
    setSelectedYear(parseIsoDate(selectedDate).getFullYear());
    setCalendarView('days');
  }, [activeField, endValue, startValue]);

  React.useEffect(() => {
    if (calendarView === 'days') {
      dayRefs.current.get(focusedDate)?.focus({preventScroll: true});
      return;
    }
    if (calendarView === 'years') {
      const currentYear = parseIsoDate(viewDate).getFullYear();
      (selectorRefs.current.get(currentYear) || selectorRefs.current.get(years[0]))?.focus({preventScroll: true});
      return;
    }
    const currentMonth = parseIsoDate(viewDate).getMonth();
    const fallbackMonth = months.find(month => month.enabled);
    (selectorRefs.current.get(currentMonth) || selectorRefs.current.get(fallbackMonth?.index))?.focus({preventScroll: true});
  }, [calendarView, focusedDate, viewDate]);

  const changeMonth = amount => {
    const nextView = toIsoDate(addMonths(viewDate, amount)).replace(/-\d{2}$/, '-01');
    const monthEnd = toIsoDate(new Date(parseIsoDate(nextView).getFullYear(), parseIsoDate(nextView).getMonth() + 1, 0));
    const nextFocus = laterDate(nextView, minimumDate);
    setViewDate(nextView);
    setFocusedDate(earlierDate(nextFocus, earlierDate(monthEnd, maximumDate)));
    setSelectedYear(parseIsoDate(nextView).getFullYear());
    setCalendarView('days');
  };

  const canChangeMonth = amount => {
    const candidate = addMonths(viewDate, amount);
    const monthStart = toIsoDate(new Date(candidate.getFullYear(), candidate.getMonth(), 1));
    const monthEnd = toIsoDate(new Date(candidate.getFullYear(), candidate.getMonth() + 1, 0));
    return (!minimumDate || monthEnd >= minimumDate) && (!maximumDate || monthStart <= maximumDate);
  };

  const moveFocus = candidate => {
    const iso = toIsoDate(candidate);
    if (!isDateWithinBounds(iso, minimumDate, maximumDate)) return;
    setFocusedDate(iso);
    setViewDate(monthStart(iso));
  };

  const selectMonth = start => {
    const firstValidDate = laterDate(start, minimumDate);
    setViewDate(start);
    setFocusedDate(earlierDate(firstValidDate, earlierDate(monthEnd(start), maximumDate)));
    setCalendarView('days');
  };

  const handleDayKeyDown = (event, date) => {
    const actions = {
      ArrowLeft: () => addDays(date, -1),
      ArrowRight: () => addDays(date, 1),
      ArrowUp: () => addDays(date, -7),
      ArrowDown: () => addDays(date, 7),
      Home: () => addDays(date, -date.getDay()),
      End: () => addDays(date, 6 - date.getDay()),
      PageUp: () => addMonths(date, event.shiftKey ? -12 : -1),
      PageDown: () => addMonths(date, event.shiftKey ? 12 : 1),
    };
    if (!actions[event.key]) return;
    event.preventDefault();
    moveFocus(actions[event.key]());
  };

  return (
    <React.Fragment>
      <div className='trip-date-picker__calendar-header'>
        <button
          type='button' className='trip-date-picker__nav' aria-label={labels.previousMonth}
          disabled={!canChangeMonth(-1)} onClick={() => changeMonth(-1)}
        >
          <Chevron direction='previous' />
        </button>
        <button
          type='button'
          className='trip-date-picker__heading'
          aria-label={`${monthFormatter.format(view)}, בחירת שנה וחודש`}
          aria-expanded={calendarView !== 'days'}
          onClick={() => setCalendarView(calendarView === 'years' ? 'days' : 'years')}
        >
          {calendarView === 'months' ? selectedYear : monthFormatter.format(view)}
        </button>
        <button
          type='button' className='trip-date-picker__nav' aria-label={labels.nextMonth}
          disabled={!canChangeMonth(1)} onClick={() => changeMonth(1)}
        >
          <Chevron direction='next' />
        </button>
      </div>
      <div className='trip-date-picker__prompt' aria-live='polite'>
        {activeField === 'departure' ? labels.selectDeparture : labels.selectReturn}
      </div>
      {calendarView === 'years' && (
        <div
          role='group'
          className='trip-date-picker__selector-grid trip-date-picker__selector-grid--years'
          aria-label='בחירת שנה'
        >
          {years.map(year => (
            <button
              key={year}
              ref={element => {
                if (element) selectorRefs.current.set(year, element);
                else selectorRefs.current.delete(year);
              }}
              type='button'
              className='trip-date-picker__selector-option'
              aria-pressed={year === parseIsoDate(viewDate).getFullYear()}
              onClick={() => {
                setSelectedYear(year);
                setCalendarView('months');
              }}
            >
              {year}
            </button>
          ))}
        </div>
      )}
      {calendarView === 'months' && (
        <div
          role='group'
          className='trip-date-picker__selector-grid'
          aria-label={`בחירת חודש לשנת ${selectedYear}`}
        >
          {months.map(month => (
            <button
              key={month.start}
              ref={element => {
                if (element) selectorRefs.current.set(month.index, element);
                else selectorRefs.current.delete(month.index);
              }}
              type='button'
              className='trip-date-picker__selector-option'
              disabled={!month.enabled}
              aria-label={`${month.label} ${selectedYear}`}
              aria-pressed={month.start === viewDate}
              onClick={() => selectMonth(month.start)}
            >
              {month.label}
            </button>
          ))}
        </div>
      )}
      {calendarView === 'days' && (
        <div
          role='grid' aria-label={activeField === 'departure' ? labels.selectDeparture : labels.selectReturn}
          className='trip-date-picker__calendar-grid' onMouseLeave={() => onHover('')}
        >
          {weekdays.map((weekday, index) => (
            <div key={`${weekday}-${index}`} role='columnheader' aria-label={weekday} className='trip-date-picker__weekday'>{weekday}</div>
          ))}
          {days.map((date, index) => {
            const iso = toIsoDate(date);
            const disabled = !isDateWithinBounds(iso, minimumDate, maximumDate);
            const isStart = iso === startValue;
            const isEnd = Boolean(rangeEnd) && iso === rangeEnd;
            const isSameDay = isStart && isEnd;
            const inRange = Boolean(startValue && rangeEnd && iso > startValue && iso < rangeEnd);
            const outsideMonth = date.getMonth() !== view.getMonth();
            const cellClasses = [
              'trip-date-picker__day-cell',
              inRange ? 'trip-date-picker__day-cell--range' : '',
              isStart && !isSameDay && index % 7 !== 6 ? 'trip-date-picker__day-cell--start' : '',
              isEnd && !isSameDay && index % 7 !== 0 ? 'trip-date-picker__day-cell--end' : '',
            ].filter(Boolean).join(' ');
            return (
              <div key={iso} role='gridcell' aria-selected={isStart || isEnd} className={cellClasses}>
                <button
                  ref={element => {
                    if (element) dayRefs.current.set(iso, element);
                    else dayRefs.current.delete(iso);
                  }}
                  type='button'
                  disabled={disabled}
                  tabIndex={iso === focusedDate ? 0 : -1}
                  aria-label={dayFormatter.format(date)}
                  aria-current={iso === toIsoDate(new Date()) ? 'date' : undefined}
                  className={`trip-date-picker__day${
                    isStart || isEnd ? ' trip-date-picker__day--endpoint' : ''
                  }${outsideMonth ? ' trip-date-picker__day--outside' : ''}`}
                  onClick={() => onChange(iso)}
                  onFocus={() => setFocusedDate(iso)}
                  onMouseEnter={() => !disabled && activeField === 'return' && onHover(iso)}
                  onKeyDown={event => handleDayKeyDown(event, date)}
                >
                  {date.getDate()}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </React.Fragment>
  );
}

RangeCalendar.propTypes = {
  activeField: PropTypes.oneOf(['departure', 'return']).isRequired,
  startValue: PropTypes.string,
  endValue: PropTypes.string,
  hoverValue: PropTypes.string,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  minYear: PropTypes.number,
  maxYear: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onHover: PropTypes.func.isRequired,
  labels: PropTypes.object.isRequired,
};

function RangeTripDatePicker({
  startValue,
  endValue,
  minDate,
  maxStartDate,
  maxEndDate,
  onStartChange,
  onEndChange,
  error,
  minYear,
  maxYear,
  startLabel,
  endLabel,
}) {
  const intl = useIntl();
  const theme = useTheme();
  const rootRef = React.useRef(null);
  const startButtonRef = React.useRef(null);
  const endButtonRef = React.useRef(null);
  const [activeField, setActiveField] = React.useState(null);
  const [hoverValue, setHoverValue] = React.useState('');
  const startIso = normalizeIso(startValue);
  const endIso = normalizeIso(endValue);
  const popupId = 'trip-date-picker-calendar';
  const errorId = 'trip-date-picker-error';
  const labels = {
    previousMonth: intl.formatMessage({id: 'tripDatePicker.previousMonth'}),
    nextMonth: intl.formatMessage({id: 'tripDatePicker.nextMonth'}),
    selectDeparture: intl.formatMessage({id: 'tripDatePicker.selectDate'}, {label: startLabel}),
    selectReturn: intl.formatMessage({id: 'tripDatePicker.selectDate'}, {label: endLabel}),
  };

  React.useEffect(() => {
    const closeOnOutsideClick = event => {
      if (!rootRef.current?.contains(event.target)) {
        setActiveField(null);
        setHoverValue('');
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  const closeAndFocus = field => {
    setActiveField(null);
    setHoverValue('');
    (field === 'departure' ? startButtonRef : endButtonRef).current?.focus({preventScroll: true});
  };

  React.useEffect(() => {
    if (!activeField) return undefined;
    const closeOnEscape = event => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeAndFocus(activeField);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [activeField]);

  const handleSelect = iso => {
    if (activeField === 'departure') {
      onStartChange(iso);
      if (!endIso || iso >= endIso) onEndChange('');
      setActiveField('return');
      setHoverValue('');
      return;
    }
    if (!startIso || iso < startIso) {
      onStartChange(iso);
      onEndChange('');
      setActiveField('return');
      setHoverValue('');
      return;
    }
    onEndChange(iso);
    closeAndFocus('return');
  };

  const toggleField = field => {
    if (activeField === field) closeAndFocus(field);
    else {
      setActiveField(field);
      setHoverValue('');
    }
  };

  const rootStyle = {
    '--trip-primary': theme.palette.primary.main,
    '--trip-primary-dark': theme.palette.primary.dark,
    '--trip-primary-contrast': theme.palette.primary.contrastText,
    '--trip-range': theme.palette.primary.lighter,
    '--trip-paper': theme.palette.background.paper,
    '--trip-border': theme.palette.divider,
    '--trip-text': theme.palette.text.primary,
    '--trip-text-secondary': theme.palette.text.secondary,
    '--trip-disabled': theme.palette.text.disabled,
    '--trip-disabled-background': theme.palette.action.disabledBackground,
    '--trip-error': theme.palette.error.main,
    '--trip-focus': alpha(theme.palette.primary.main, 0.3),
    '--trip-shadow': theme.shadows[4],
    '--trip-font-regular': theme.typography.fontWeightRegular,
    '--trip-font-medium': theme.typography.fontWeightMedium,
    '--trip-font-bold': theme.typography.fontWeightBold,
    '--trip-label-size': theme.typography.pxToRem(15),
    '--trip-value-size': theme.typography.pxToRem(19),
    '--trip-small-size': theme.typography.pxToRem(12),
    '--trip-day-size': theme.typography.pxToRem(14),
  };

  const renderField = (field, value, label, buttonRef, testId) => {
    const isActive = activeField === field;
    return (
      <div className='trip-date-picker__field'>
        <div id={`${testId}-label`} className='trip-date-picker__label'>{label}</div>
        <button
          ref={buttonRef}
          type='button'
          data-testid={testId}
          className={`trip-date-picker__trigger${
            isActive ? ' trip-date-picker__trigger--active' : ''
          }${error ? ' trip-date-picker__trigger--error' : ''}`}
          aria-labelledby={`${testId}-label`}
          aria-describedby={error ? errorId : undefined}
          aria-invalid={error || undefined}
          aria-expanded={isActive}
          aria-controls={isActive ? popupId : undefined}
          onClick={() => toggleField(field)}
        >
          <span className='trip-date-picker__icon'><CalendarIcon /></span>
          <span className='trip-date-picker__value'>{value ? formatIsoDate(value) : 'DD/MM/YYYY'}</span>
        </button>
      </div>
    );
  };

  return (
    <div
      ref={rootRef}
      data-testid='trip-date-picker'
      data-validation-field='policyStartDate policyEndDate'
      className='trip-date-picker'
      style={rootStyle}
    >
      <div className='rd-dates-grid trip-date-picker__fields'>
        {renderField('departure', startIso, startLabel, startButtonRef, 'date-picker-start')}
        {renderField('return', endIso, endLabel, endButtonRef, 'date-picker-end')}
      </div>
      {error && <span id={errorId} className='trip-date-picker__sr-only'>{intl.formatMessage({id: 'tripDatePicker.invalidRange'})}</span>}
      {activeField && (
        <div
          id={popupId}
          role='dialog'
          aria-label={activeField === 'departure' ? labels.selectDeparture : labels.selectReturn}
          className='trip-date-picker__popup'
        >
          <RangeCalendar
            activeField={activeField}
            startValue={startIso}
            endValue={endIso}
            hoverValue={hoverValue}
            minDate={minDate}
            maxDate={activeField === 'departure' ? maxStartDate : maxEndDate}
            minYear={minYear}
            maxYear={maxYear}
            onChange={handleSelect}
            onHover={setHoverValue}
            labels={labels}
          />
        </div>
      )}
    </div>
  );
}

RangeTripDatePicker.propTypes = {
  startValue: PropTypes.string,
  endValue: PropTypes.string,
  minDate: PropTypes.string,
  maxStartDate: PropTypes.string,
  maxEndDate: PropTypes.string,
  onStartChange: PropTypes.func.isRequired,
  onEndChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  minYear: PropTypes.number,
  maxYear: PropTypes.number,
  startLabel: PropTypes.string.isRequired,
  endLabel: PropTypes.string.isRequired,
};

/**
 * Travel-owned range policy composed from Common date inputs.
 */
const TripDatePicker = ({
  startValue,
  endValue,
  minDate,
  maxStartDate,
  maxEndDate,
  onStartChange,
  onEndChange,
  error,
  minYear,
  maxYear,
  popupStrategy = 'viewport',
  startLabel,
  endLabel,
  selectionMode = 'independent',
}) => {
  const intl = useIntl();
  const resolvedStartLabel = startLabel || intl.formatMessage({id: 'tripDatePicker.startLabel'});
  const resolvedEndLabel = endLabel || intl.formatMessage({id: 'tripDatePicker.endLabel'});
  const startIso = startValue ? startValue.slice(0, 10) : '';
  const endIso = endValue ? endValue.slice(0, 10) : '';

  if (selectionMode === 'range') {
    return (
      <RangeTripDatePicker
        startValue={startValue}
        endValue={endValue}
        minDate={minDate}
        maxStartDate={maxStartDate}
        maxEndDate={maxEndDate}
        onStartChange={onStartChange}
        onEndChange={onEndChange}
        error={error}
        minYear={minYear}
        maxYear={maxYear}
        startLabel={resolvedStartLabel}
        endLabel={resolvedEndLabel}
      />
    );
  }

  const handleStartChange = (iso) => {
    onStartChange(iso);
    if (!endIso || iso >= endIso) onEndChange('');
  };

  return (
    <div data-testid='trip-date-picker'>
      <div className='rd-dates-grid trip-date-picker__fields'>
        <FormField label={resolvedStartLabel}>
          <DateInput
            className='trip-date-picker__independent-input'
            inputStyle={{fontWeight: 700, textAlign: 'center'}}
            data-testid='date-picker-start'
            value={startIso}
            minDate={minDate}
            maxDate={maxStartDate}
            onChange={handleStartChange}
            invalid={error}
            invalidMessage={intl.formatMessage({id: 'tripDatePicker.invalidStartDate'})}
            calendarLabel={intl.formatMessage({id: 'tripDatePicker.selectDate'}, {label: resolvedStartLabel})}
            openCalendarLabel={intl.formatMessage({id: 'tripDatePicker.openCalendar'}, {label: resolvedStartLabel})}
            minYear={minYear}
            maxYear={maxYear}
            popupStrategy={popupStrategy}
            locale='he-IL'
          />
        </FormField>
        <FormField label={resolvedEndLabel}>
          <DateInput
            className='trip-date-picker__independent-input'
            inputStyle={{fontWeight: 700, textAlign: 'center'}}
            data-testid='date-picker-end'
            value={endIso}
            minDate={startIso || minDate}
            maxDate={maxEndDate}
            onChange={onEndChange}
            invalid={error}
            invalidMessage={intl.formatMessage({id: 'tripDatePicker.invalidEndDate'})}
            calendarLabel={intl.formatMessage({id: 'tripDatePicker.selectDate'}, {label: resolvedEndLabel})}
            openCalendarLabel={intl.formatMessage({id: 'tripDatePicker.openCalendar'}, {label: resolvedEndLabel})}
            minYear={minYear}
            maxYear={maxYear}
            popupStrategy={popupStrategy}
            locale='he-IL'
          />
        </FormField>
      </div>
    </div>
  );
};

TripDatePicker.propTypes = {
  endValue: PropTypes.string,
  error: PropTypes.bool,
  maxEndDate: PropTypes.string,
  maxStartDate: PropTypes.string,
  minYear: PropTypes.number,
  maxYear: PropTypes.number,
  minDate: PropTypes.string,
  onEndChange: PropTypes.func.isRequired,
  onStartChange: PropTypes.func.isRequired,
  startValue: PropTypes.string,
  startLabel: PropTypes.string,
  endLabel: PropTypes.string,
  popupStrategy: PropTypes.oneOf(['inline', 'viewport']),
  selectionMode: PropTypes.oneOf(['independent', 'range']),
};

export default TripDatePicker;
